import React from 'react';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import { getSemanticSurfaceTokens, layoutGap } from '../../../theme/theme';
import {
  describeSetupRevisionPhase,
  resolveSetupRevisionPhase,
} from '../../lib/setupRevision/revisionPhase';
import type { SetupRevisionStatus } from '../../lib/setupRevision/revisionState';
import { ProgressMeter } from '../progress';
import { BodyText } from '../typography';

export interface SetupRevisionProgressProps {
  status: SetupRevisionStatus;
}

/**
 * Says in words which step of saving a setup revision is running, so progress
 * can be read without watching the bar move.
 */
export const SetupRevisionProgress: React.FC<SetupRevisionProgressProps> = ({ status }) => {
  const theme = useTheme();
  const colors = getSemanticSurfaceTokens(theme);
  const phase = resolveSetupRevisionPhase(status);

  if (!phase) {
    return null;
  }

  return (
    <Box
      role="status"
      aria-live="polite"
      aria-label={`PipeEX setup revision progress: ${describeSetupRevisionPhase(phase)}`}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: layoutGap.dense,
        px: 2,
        py: 1,
        borderBottom: `1px solid ${colors.border.light}`,
        bgcolor: colors.background.panel,
      }}
    >
      <BodyText sx={{ color: colors.text.primary, fontWeight: 600 }}>
        {describeSetupRevisionPhase(phase)}
      </BodyText>
      <BodyText secondary sx={{ color: colors.text.secondary }}>
        {phase.detail}
      </BodyText>
      {/* The words above already state the step, so the bar is decoration. */}
      <ProgressMeter
        testId="setup-revision-progress-bar"
        presentational
        value={Math.round((phase.step / phase.totalSteps) * 100)}
        height={4}
      />
    </Box>
  );
};

export default SetupRevisionProgress;
